import {
    Container,
    MaterialContext,
    Row,
    Column,
    ThemeContext,
} from '@wonderlandengine/react-ui/components';
import {
    FlexDirection,
    Justify,
    PositionType,
    ReactUiBase,
} from '@wonderlandengine/react-ui';
import {useState, useEffect, createContext} from 'react';
import {colorSwatch} from './utils/colorSwatch.js';
import {Material, Mesh, Object3D, Texture, WonderlandEngine} from '@wonderlandengine/api';
import {Window} from './components/window.js';
import {property} from '@wonderlandengine/api/decorators.js';
import {MenuButton} from './components/menu-button.js';
import {
    RotBMaterialContext,
    RotBMaterialContextValue,
} from './utils/rotb-material-context.js';
import {updateAnimations} from './utils/animation-context.js';
import {MenuPanels, UIModel} from '../models/UIModel.js';
import {goToMenu} from './utils/goToMenu.js';
import {goToGame} from './utils/goToGame.js';
import {goToMainMenu} from './utils/gotoMainMenu.js';
import {goToPreviousMenu} from './utils/goToPreviousMenu.js';
import {OptionsMenuPanel} from './panels/options-menu-panel.js';
import {HUDPanel} from './panels/panel-hud.js';
import {GameOverPanel} from './panels/game-over-panel.js';
import {GameFlowController} from '../components/game-flow-controller.js';
import {ShoppeMenu} from './panels/shoppe-menu.js';
import {ShoppePowerups} from './panels/shoppe-powerup.js';
import {ShoppePerks} from './panels/shoppe-perks.js';
import {ShoppeMalwear} from './panels/shoppe-malwear.js';
import {ShoppeOffers} from './panels/shoppe-offers.js';
import {ShoppeCoins} from './panels/shoppe-coins.js';
import {ComingSoon} from './panels/coming-soon.js';
import {SliderComponent} from './components/SliderComponent.js';
import {SpawnPieceButton} from '../components/spawn-piece-button.js';
import {MalUIComponent} from './components/mal-ui.js';
import {MainMenu} from './panels/main-menu.js';
import {UiTexturesManager} from './components/ui-textures-manager.js';
import {PopupWindow} from './panels/popup-window.js';
import {TutorialHUDPanel} from './panels/panel-hud-tutorial.js';
import {BombButtonTutorial} from '../components/bomb-button-tutorial.js';
import {WLReactComp} from './components/WLReactComponent.js';
import {UpdateFrameContext, UpdateFrameContextInt} from './utils/update-frame-context.js';
import {
    ConfirmationDialog,
    ConfirmationDialogController,
} from './panels/confirmation-dialog.js';
import {FruitSmasherButton} from '../components/fruit-smasher-button.js';
import {UnlockedSomething} from './panels/unlocked-something.js';
import {Localization} from '../utils/Localization.js';
import {PauseWindow} from './panels/pause-window.js';
import {Leaderboard} from './panels/leaderboard.js';
import {ModeSelectPanel} from './panels/mode-select-panel.js';
import {MissionRequirements} from './panels/mission-requirements.js';
import {Notification} from './panels/notification.js';
import {Rewards} from './panels/rewards.js';
import {SkipMission} from './panels/skip-mission.js';
import {Sounds} from '../enums/Sounds.js';
import {CreditsPanel} from './panels/credits-panel.js';
import {MissionCompleteNotificationModel} from '../models/MissionCompleteNotificationModel.js';
import {MissionAchievementNotification} from './panels/mission-achieved-notification.js';
import {AchievementsPanel} from './panels/achievements.js';
import {GetDailyReward} from './panels/get-daily-reward.js';
import {StatsPanel} from './panels/stats-panel.js';
import {MoreGamesPanel} from './panels/more-games-panel.js';
import {LoadoutPowerups} from './panels/loadout.js';
import {ShoppePowerupsPopup} from './panels/shoppe-powerup-popup.js';
import {LoadoutPerks} from './panels/loadout2.js';
import {StageSelectPanel} from './panels/stage-select-panel.js';
import {JiggleComponent} from './components/jiggle-component.js';

function isMenuActive(menu: MenuPanels) {
    return UIModel.instance.isMenuActive(menu);
}
function isHUDActive() {
    return UIModel.instance.isHUDActive();
}
function isTutorialHudActive() {
    return UIModel.instance.isTutorialHudActive();
}

const App = (props: {comp: MainUI}) => {
    const comp = props.comp;
    const theme = {};
    const rotbMats: RotBMaterialContextValue = {
        windowMaterial: comp.defaultWindow,
        windowShadowMaterial: comp.defaultWindowShadow,
        buttonMaterial: comp.baseMenuButton,
        buttonShadowMaterial: comp.baseMenuButtonShadow,
        titleBackgroundMaterial: comp.titleBackground,
        gradientMaterial: comp.gradientMaterial,
        hudBarMaterial: comp.hudBarMaterial,
        uiTextShadowMaterial: comp.uiTextShadowMaterial,
        whiteBevelMaterial: comp.whiteBevelMaterial,
        hudWindowMaterial: comp.hudWindowMaterial,
        malReference: comp.malReference,
        planeReference: comp.planeReference,
        regularTextMaterial: comp.regularTextMaterial,
        cooldownMaterial: comp.cooldownMaterial,
        circleTexture: comp.circleTexture,
        buttonSimple: comp.buttonSimple,
        wordBalloonTail: comp.wordBalloonTail,
        wordBalloonTailRotated: comp.wordBalloonTailRotated,
        wordBalloonTailFlipped: comp.wordBalloonTailFlipped,
        panelMaterialTexturedAlpha: comp.panelMaterialTexturedAlpha,
    };

    const [mainMenuActive, setMainMenuActive] = useState(isMenuActive(MenuPanels.MainMenu));
    const [optionsMenuActive, setOptionsMenuActive] = useState(
        isMenuActive(MenuPanels.Options)
    );
    const [gameOverMenuActive, setGameOverMenuActive] = useState(
        isMenuActive(MenuPanels.GameOver)
    );
    const [shoppeActive, setShoppeActive] = useState(isMenuActive(MenuPanels.Shoppe));
    const [shoppePowerUpsActive, setShoppePowerUpsActive] = useState(
        isMenuActive(MenuPanels.StorePowerups)
    );
    const [shoppePerksActive, setShoppePerksActive] = useState(
        isMenuActive(MenuPanels.StorePerks)
    );
    const [shoppeOffersActive, setShoppeOffersActive] = useState(
        isMenuActive(MenuPanels.StoreSpecialOffers)
    );
    const [shoppeMalwearActive, setShoppeMalwearActive] = useState(
        isMenuActive(MenuPanels.Malwear)
    );
    const [shoppeCoinsActive, setShoppeCoinsActive] = useState(
        isMenuActive(MenuPanels.StoreCoins)
    );
    const [pauseActive, setPauseActive] = useState(isMenuActive(MenuPanels.Pause));

    const [mainHudActive, setMainHudActive] = useState(false);
    const [leaderboardActive, setLeaderboardActive] = useState(
        isMenuActive(MenuPanels.Leaderboard)
    );
    const [missionsActive, setMissionsActive] = useState(
        isMenuActive(MenuPanels.MissionRequirements)
    );
    const [achievementsActive, setAchievementsActive] = useState(
        isMenuActive(MenuPanels.Achievements)
    );
    const [statsActive, setStatsActive] = useState(isMenuActive(MenuPanels.Stats));
    const [modeSelectActive, setModeSelectActive] = useState(
        isMenuActive(MenuPanels.ModeSelect)
    );
    const [stageSelectActive, setStageSelectActive] = useState(
        isMenuActive(MenuPanels.StageSelect)
    );
    const [gamesActive, setGamesActive] = useState(isMenuActive(MenuPanels.Games));
    const [rewardsActive, setRewardsActive] = useState(isMenuActive(MenuPanels.Rewards));
    const [dailyRewardActive, setDailyRewardActive] = useState(
        isMenuActive(MenuPanels.GetDailyReward)
    );
    const [creditsActive, setCreditsActive] = useState(isMenuActive(MenuPanels.Credits));
    const [tutorialHudActive, setTutorialHudActive] = useState(false);
    const [popupActive, setPopupActive] = useState(UIModel.instance.isPopupActive());
    const [unlockPopupActive, setUnlockPopupActive] = useState(
        isMenuActive(MenuPanels.UnlockedSometing)
    );
    const [skipMissionAction, setSkipMissionAction] = useState(
        isMenuActive(MenuPanels.SkipMission)
    );
    const [loadoutActive, setLoadoutActive] = useState(isMenuActive(MenuPanels.Loadout));
    const [loadout2Active, setLoadout2Active] = useState(isMenuActive(MenuPanels.Loadout2));
    const [shoppePowerUpsPopupActive, setShoppePowerUpsPopupActive] = useState(
        isMenuActive(MenuPanels.StorePowerupsPopup)
    );

    useEffect(() => {
        const onKeyPressed = (e: KeyboardEvent) => {
            if (e.key === '1') {
                GameFlowController.instance.gameOver();
            }
        };
        document.addEventListener('keydown', onKeyPressed);
        return () => {
            document.removeEventListener('keydown', onKeyPressed);
        };
    }, []);

    useEffect(() => {
        const onUiPanelChange = (prop: string) => {
            if (prop === 'PopupActive') {
                setPopupActive(UIModel.instance.isPopupActive());
            }
            if (prop !== UIModel.ACTIVE_MENU_CHANGED) return;
            setMainMenuActive(isMenuActive(MenuPanels.MainMenu));
            setOptionsMenuActive(isMenuActive(MenuPanels.Options));
            setGameOverMenuActive(isMenuActive(MenuPanels.GameOver));
            setShoppeActive(isMenuActive(MenuPanels.Shoppe));
            setShoppePowerUpsActive(isMenuActive(MenuPanels.StorePowerups));
            setShoppePerksActive(isMenuActive(MenuPanels.StorePerks));
            setShoppeOffersActive(isMenuActive(MenuPanels.StoreSpecialOffers));
            setShoppeCoinsActive(isMenuActive(MenuPanels.StoreCoins));
            setShoppeMalwearActive(isMenuActive(MenuPanels.Malwear));
            setLeaderboardActive(isMenuActive(MenuPanels.Leaderboard));
            setMissionsActive(isMenuActive(MenuPanels.MissionRequirements));
            setAchievementsActive(isMenuActive(MenuPanels.Achievements));
            setStatsActive(isMenuActive(MenuPanels.Stats));
            setGamesActive(isMenuActive(MenuPanels.Games));
            setCreditsActive(isMenuActive(MenuPanels.Credits));
            setUnlockPopupActive(isMenuActive(MenuPanels.UnlockedSometing));
            setPauseActive(isMenuActive(MenuPanels.Pause));
            setModeSelectActive(isMenuActive(MenuPanels.ModeSelect));
            setStageSelectActive(isMenuActive(MenuPanels.StageSelect));
            setRewardsActive(isMenuActive(MenuPanels.Rewards));
            setSkipMissionAction(isMenuActive(MenuPanels.SkipMission));
            setDailyRewardActive(isMenuActive(MenuPanels.GetDailyReward));
            setMainHudActive(isHUDActive());
            setTutorialHudActive(isTutorialHudActive());
            setLoadoutActive(isMenuActive(MenuPanels.Loadout));
            setLoadout2Active(isMenuActive(MenuPanels.Loadout2));
            setShoppePowerUpsPopupActive(isMenuActive(MenuPanels.StorePowerupsPopup));
        };

        UIModel.instance.propertyChanged.add(onUiPanelChange);

        // return cleanup function.
        return () => {
            UIModel.instance.propertyChanged.remove(onUiPanelChange);
        };
    }, []);

    useEffect(() => {
        // UI is fully loaded after first render
        UIModel.instance.setUILoaded();
    }, []);

    return (
        <MaterialContext.Provider value={comp}>
            <RotBMaterialContext.Provider value={rotbMats}>
                <ThemeContext.Provider value={theme}>
                    <Container position={PositionType.Absolute} width="100%" height="100%">
                        {rewardsActive && <Rewards></Rewards>}
                        {skipMissionAction && <SkipMission></SkipMission>}
                        <MissionAchievementNotification></MissionAchievementNotification>
                        <Notification></Notification>
                        {!pauseActive && <PopupWindow z={0.02}></PopupWindow>}
                        <ConfirmationDialog></ConfirmationDialog>
                        {unlockPopupActive && <UnlockedSomething></UnlockedSomething>}
                        {dailyRewardActive && <GetDailyReward></GetDailyReward>}
                        {!popupActive && (
                            //TK: Due to some Z-issues the UI hides for now when a popup is shown.
                            <Container
                                position={PositionType.Absolute}
                                width="100%"
                                height="100%"
                            >
                                {mainMenuActive && <MainMenu></MainMenu>}

                                {optionsMenuActive && (
                                    <Container
                                        position={PositionType.Absolute}
                                        width="100%"
                                        height="100%"
                                        flexDirection={FlexDirection.Row}
                                        justifyContent={Justify.Center}
                                    >
                                        <Column marginTop={36}>
                                            <Window>
                                                <OptionsMenuPanel></OptionsMenuPanel>
                                            </Window>
                                            <Row justifyContent={Justify.SpaceAround}>
                                                <MenuButton
                                                    width={250}
                                                    height={54}
                                                    margin={6}
                                                    color={colorSwatch.AcceptButton}
                                                    fontSize={32}
                                                    iconWidth={38}
                                                    iconHeight={40}
                                                    sound={Sounds.cancel}
                                                    onClick={() => {
                                                        goToPreviousMenu(
                                                            MenuPanels.Options
                                                        );
                                                    }}
                                                >
                                                    {Localization.instance.get(
                                                        'PurchaseFailedDialogAccept'
                                                    )}
                                                </MenuButton>
                                            </Row>
                                        </Column>
                                    </Container>
                                )}
                                {gameOverMenuActive && (
                                    <Container
                                        position={PositionType.Absolute}
                                        width="100%"
                                        height="100%"
                                        flexDirection={FlexDirection.Row}
                                        justifyContent={Justify.Center}
                                    >
                                        <Column marginTop={36}>
                                            <Window color={colorSwatch.WindowGameover}>
                                                <GameOverPanel></GameOverPanel>
                                            </Window>
                                            <Row justifyContent={Justify.SpaceAround}>
                                                <MenuButton
                                                    width={250}
                                                    height={54}
                                                    margin={6}
                                                    color={colorSwatch.CancelButton}
                                                    fontSize={28}
                                                    icon={UiTexturesManager.instance.home}
                                                    iconWidth={38}
                                                    iconHeight={40}
                                                    sound={Sounds.cancel}
                                                    onClick={() => goToMainMenu()}
                                                    text={Localization.instance.get('Home')}
                                                ></MenuButton>
                                                <MenuButton
                                                    width={250}
                                                    height={54}
                                                    margin={6}
                                                    color={colorSwatch.AcceptButton}
                                                    fontSize={32}
                                                    iconWidth={38}
                                                    iconHeight={40}
                                                    onClick={({e}) =>
                                                        goToMenu(MenuPanels.Loadout)
                                                    }
                                                    animate={true}
                                                    icon={UiTexturesManager.instance.arrow}
                                                    text={Localization.instance.get('Play')}
                                                ></MenuButton>
                                            </Row>
                                        </Column>
                                    </Container>
                                )}
                                {modeSelectActive && <ModeSelectPanel></ModeSelectPanel>}
                                {stageSelectActive && <StageSelectPanel></StageSelectPanel>}
                                {shoppeActive && <ShoppeMenu></ShoppeMenu>}
                                {shoppePowerUpsActive && <ShoppePowerups></ShoppePowerups>}
                                {shoppePerksActive && <ShoppePerks></ShoppePerks>}
                                {shoppeMalwearActive && <ShoppeMalwear></ShoppeMalwear>}
                                {shoppeOffersActive && <ShoppeOffers></ShoppeOffers>}
                                {shoppeCoinsActive && <ShoppeCoins></ShoppeCoins>}
                                {pauseActive && <PauseWindow></PauseWindow>}
                                {leaderboardActive && <Leaderboard></Leaderboard>}

                                {missionsActive && (
                                    <MissionRequirements></MissionRequirements>
                                )}
                                {achievementsActive && (
                                    <AchievementsPanel></AchievementsPanel>
                                )}
                                {statsActive && <StatsPanel></StatsPanel>}
                                {gamesActive && <MoreGamesPanel></MoreGamesPanel>}
                                {creditsActive && <CreditsPanel></CreditsPanel>}
                                {mainHudActive && <HUDPanel></HUDPanel>}
                                {tutorialHudActive && <TutorialHUDPanel></TutorialHUDPanel>}
                                {loadoutActive && <LoadoutPowerups></LoadoutPowerups>}
                                {loadout2Active && <LoadoutPerks></LoadoutPerks>}
                                {shoppePowerUpsPopupActive && (
                                    <ShoppePowerupsPopup></ShoppePowerupsPopup>
                                )}
                            </Container>
                        )}
                    </Container>
                </ThemeContext.Provider>
            </RotBMaterialContext.Provider>
        </MaterialContext.Provider>
    );
};

//TK: I don't 100% like this constructions. Maybe we should move
// this to the ReactUiBase class? And make sure this keeps working
// correctly when we have multiple instances of the MainUI component.
// right now weird things might happen if we have multiple instances, but
// since this game only hase 1 we are fine (for now).
const frameUpdateImplementation = new UpdateFrameContextInt();
export const FrameUpdateContext = createContext<UpdateFrameContext>(
    frameUpdateImplementation
);

/**
 * react-ui
 */
export class MainUI extends ReactUiBase {
    static TypeName = 'main-menu';
    static InheritProperties = true;

    static onRegister(engine: WonderlandEngine): void {
        engine.registerComponent(SliderComponent);
        engine.registerComponent(SpawnPieceButton);
        engine.registerComponent(BombButtonTutorial);
        engine.registerComponent(MalUIComponent);
        engine.registerComponent(WLReactComp);
        engine.registerComponent(JiggleComponent);
    }

    @property.material({required: true})
    defaultWindow!: Material;

    @property.material({required: true})
    defaultWindowShadow!: Material;

    @property.material({required: true})
    baseMenuButton!: Material;

    @property.material({required: true})
    baseMenuButtonShadow!: Material;

    @property.material({required: true})
    titleBackground!: Material;

    @property.material({required: true})
    gradientMaterial!: Material;

    @property.material({required: true})
    hudBarMaterial!: Material;

    @property.material({required: true})
    uiTextShadowMaterial!: Material;

    @property.material({required: true})
    regularTextMaterial: Material;

    @property.material({required: true})
    whiteBevelMaterial!: Material;

    @property.material({required: true})
    hudWindowMaterial!: Material;

    @property.material({required: true})
    cooldownMaterial!: Material;

    @property.object({required: true})
    malReference!: Object3D;

    @property.mesh({required: true})
    planeReference!: Mesh;

    @property.texture()
    circleTexture?: Texture;
    @property.texture()
    buttonSimple: Texture;
    @property.texture()
    wordBalloonTail: Texture;
    @property.texture()
    wordBalloonTailRotated: Texture;
    @property.texture()
    wordBalloonTailFlipped: Texture;

    @property.material({required: true})
    panelMaterialTexturedAlpha: Material;

    override update(dt: number) {
        super.update();
        frameUpdateImplementation.relativeScale = this.scaling;
        frameUpdateImplementation.triggerUpdate(dt);
        updateAnimations(dt);
    }

    render() {
        return <App comp={this} />;
    }
}
